import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, ExternalLink } from 'lucide-react';
import courses from '../data/courses.json';
import courseDetails from '../data/courseDetails';
import {
  buildFlutterwaveConfig,
  isFlutterwaveEnabled,
  loadFlutterwaveScript,
} from '../utils/flutterwave';

const coverMap = {
  'course-1.png': () => require('../assets/course-1.png'),
  'course-2.png': () => require('../assets/course-2.png'),
  'course-3.png': () => require('../assets/course-3.png'),
};

const resolveCover = (cover) => {
  if (!cover || typeof cover !== 'string') return null;

  const trimmed = cover.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  const loader = coverMap[trimmed];
  if (!loader) {
    try {
      return require(`../assets/${trimmed}`);
    } catch {
      return null;
    }
  }

  try {
    return loader();
  } catch {
    return null;
  }
};

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const course = courses.find((item) => item.id === courseId);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  if (!course) {
    return (
      <main className="mx-3 md:mx-10 lg:mx-16 py-16">
        <h1 className="text-3xl font-inter font-semibold text-acBlack">Course not found</h1>
        <p className="mt-3 text-acGray font-inter">
          The course you are trying to access does not exist yet.
        </p>
        <Link
          to="/#courses"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-acBlack text-white text-sm font-inter font-semibold"
        >
          <ArrowLeft size={16} />
          Back to Courses
        </Link>
      </main>
    );
  }

  const details = courseDetails[course.id];
  const cover = resolveCover(course.cover);
  const isFlutterwaveCourse = isFlutterwaveEnabled(course);

  const openEnrollModal = () => {
    setPaymentError('');
    setIsEnrollModalOpen(true);
  };

  const closeEnrollModal = () => {
    if (isProcessingPayment) return;
    setIsEnrollModalOpen(false);
  };

  const handleFlutterwaveCheckout = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setPaymentError('Enter your email to continue checkout.');
      return;
    }

    setPaymentError('');
    setPaymentStatus('Preparing secure checkout...');
    setIsProcessingPayment(true);

    try {
      await loadFlutterwaveScript();

      if (!window.FlutterwaveCheckout) {
        throw new Error('Flutterwave checkout is unavailable right now. Please try again.');
      }

      let paymentCompleted = false;
      const config = buildFlutterwaveConfig({
        course,
        customer: {
          email: trimmedEmail,
          name: name.trim() || 'Vali Courses Learner',
        },
      });

      window.FlutterwaveCheckout({
        ...config,
        callback: (response) => {
          paymentCompleted = true;

          if (response?.status === 'successful' || response?.status === 'completed') {
            setPaymentStatus('Payment submitted successfully. Check your email for next steps.');
            setPaymentError('');
            setIsEnrollModalOpen(false);
          } else {
            setPaymentStatus('');
            setPaymentError('Payment was not successful. Please try again.');
          }
          setIsProcessingPayment(false);
        },
        onclose: () => {
          if (!paymentCompleted) {
            setPaymentStatus('');
            setPaymentError('Payment window was closed before completion.');
          }
          setIsProcessingPayment(false);
        },
      });
    } catch (error) {
      setPaymentStatus('');
      setPaymentError(error.message || 'Could not start payment.');
      setIsProcessingPayment(false);
    }
  };

  return (
    <main className="bg-white">
      <section className="mx-3 md:mx-10 lg:mx-16 py-10 md:py-14">
        <Link to="/#courses" className="inline-flex items-center gap-2 text-sm text-acBlack font-inter">
          <ArrowLeft size={16} />
          Back to Courses
        </Link>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
          <div className="relative rounded-2xl overflow-hidden min-h-[260px] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700">
            {cover && (
              <img src={cover} alt={course.title} className="absolute inset-0 w-full h-full object-cover z-0" />
            )}
            {cover && <div className="absolute inset-0 bg-black/45 z-10" />}
            <div className="absolute top-4 left-4 bg-orange-500 text-white text-xs font-inter font-semibold px-3 py-1 rounded-full z-20">
              Recommended
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-100 text-acBlack text-xs font-inter px-3 py-1 rounded-full">
              <BadgeCheck size={14} className="text-blue-600" />
              {course.flag}
            </div>
            <h1 className="mt-4 text-3xl md:text-4xl font-inter font-semibold text-acBlack leading-tight">
              {course.title}
            </h1>
            <p className="mt-3 text-base text-gray-600 font-inter leading-relaxed">{course.description}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              {course.categories.map((tag) => (
                <span key={tag} className="text-xs font-inter bg-gray-100 text-acBlack px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
              <span className="text-xs font-inter bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
                {course.outcome}
              </span>
            </div>

            <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
              <div className="text-2xl font-inter font-semibold text-acBlack">{course.price}</div>
              {isFlutterwaveCourse ? (
                <button
                  type="button"
                  onClick={openEnrollModal}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-acBlack text-white text-sm font-inter font-semibold hover:opacity-90 transition"
                >
                  Enroll Now
                </button>
              ) : course.url ? (
                <a
                  href={course.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-acBlack text-white text-sm font-inter font-semibold hover:opacity-90 transition"
                >
                  Enroll Now
                  <ExternalLink size={16} />
                </a>
              ) : null}
            </div>
            {paymentError ? <p className="mt-3 text-sm text-red-600 font-inter">{paymentError}</p> : null}
            {paymentStatus ? <p className="mt-3 text-sm text-green-700 font-inter">{paymentStatus}</p> : null}
          </div>
        </div>
      </section>

      {details && (
        <section className="mx-3 md:mx-10 lg:mx-16 pb-16 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-gray-50 border border-gray-100 rounded-2xl p-6 md:p-8">
              <h2 className="text-2xl font-inter font-semibold text-acBlack">Course Overview</h2>
              <p className="mt-3 text-sm md:text-base text-gray-700 font-inter leading-relaxed">
                {details.overview}
              </p>

              <h3 className="mt-7 text-lg font-inter font-semibold text-acBlack">What you will achieve</h3>
              <ul className="mt-3 space-y-2 text-sm md:text-base text-gray-700 font-inter">
                {details.outcomes.map((item) => (
                  <li key={item}>• {item}</li>
                ))}
              </ul>

              {details.modules && details.modules.length > 0 && (
                <>
                  <h3 className="mt-7 text-lg font-inter font-semibold text-acBlack">Course modules</h3>
                  <div className="mt-3 space-y-4">
                    {details.modules.map((module) => (
                      <div key={module.title} className="bg-white border border-gray-100 rounded-xl p-4">
                        <p className="text-sm md:text-base font-inter font-semibold text-acBlack">
                          {module.title}
                        </p>
                        <ul className="mt-2 space-y-1 text-sm text-gray-700 font-inter">
                          {module.topics.map((topic) => (
                            <li key={topic}>• {topic}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <h3 className="mt-7 text-lg font-inter font-semibold text-acBlack">Course format</h3>
              <ul className="mt-3 space-y-2 text-sm md:text-base text-gray-700 font-inter">
                {details.courseFormat.map((item) => (
                  <li key={item}>• {item}</li>
                ))}
              </ul>
            </div>

            <aside className="bg-white border border-gray-100 rounded-2xl p-6 md:p-8">
              <h3 className="text-lg font-inter font-semibold text-acBlack">Who this is for</h3>
              <ul className="mt-3 space-y-2 text-sm text-gray-700 font-inter">
                {details.whoItsFor.map((item) => (
                  <li key={item}>• {item}</li>
                ))}
              </ul>

              {details.delivery && (
                <div className="mt-6 p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-inter">Course delivery</p>
                  <p className="mt-1 text-sm font-inter font-semibold text-acBlack">{details.delivery.mode}</p>
                  <p className="mt-2 text-sm text-gray-700 font-inter">{details.delivery.note}</p>
                  <p className="mt-2 text-sm text-gray-700 font-inter">{details.delivery.support}</p>
                </div>
              )}

              <div className="mt-6 p-4 rounded-xl bg-blue-50 text-blue-900 text-sm font-inter leading-relaxed">
                {details.callout}
              </div>
            </aside>
          </div>
        </section>
      )}

      {isFlutterwaveCourse && isEnrollModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
            <h3 className="text-xl font-inter font-semibold text-acBlack">Enroll Now</h3>
            <p className="mt-2 text-sm text-gray-600 font-inter">
              Enter your details to continue to payment.
            </p>

            <div className="mt-5 space-y-3">
              <div>
                <label className="block text-sm font-inter text-acBlack mb-1" htmlFor="course-name-input">
                  Name
                </label>
                <input
                  id="course-name-input"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Your name"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2 text-sm font-inter text-acBlack outline-none focus:border-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-inter text-acBlack mb-1" htmlFor="course-email-input">
                  Email
                </label>
                <input
                  id="course-email-input"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2 text-sm font-inter text-acBlack outline-none focus:border-gray-500"
                />
              </div>
            </div>

            {paymentError ? <p className="mt-3 text-sm text-red-600 font-inter">{paymentError}</p> : null}

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeEnrollModal}
                disabled={isProcessingPayment}
                className="px-4 py-2 rounded-full bg-gray-100 text-acBlack text-sm font-inter font-medium hover:bg-gray-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFlutterwaveCheckout}
                disabled={isProcessingPayment}
                className="px-5 py-2 rounded-full bg-acBlack text-white text-sm font-inter font-semibold hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isProcessingPayment ? 'Processing...' : 'Pay Now'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default CourseDetailPage;
